import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  /** すでに前の版に同意している人（＝版が上がった）か。文面を変えるためだけに使う。 */
  readonly renewal: boolean;
  readonly busy: boolean;
  readonly error: string;
  readonly onAgree: () => void;
  readonly onLater: () => void;
}

/**
 * 利用規約とプライバシーポリシーへの同意（doc/AUTH.md §13）。
 *
 * 出るのは **アカウントができた直後**（初回ログイン）と、**版が上がったとき** だけ。
 * すでにいまの版に同意している人には出ない（`GET /v1/me` の `terms.current`）。
 * 診断を受ける・作るだけなら同意は求めない。ここは「アカウントを持つ人」への確認。
 *
 * チェックボックス 1 つで取る（「続けると同意したものとみなす」にしない。あとから
 * 「見ていない」と言われないよう、何の版に同意したかを記録するため）。
 * リンクは新しいタブで開く — このダイアログを閉じさせないため。
 *
 * 「あとで」でも締め出さない。同意が要る操作を足すときは、**サーバ側**
 * （services/api/src/lib/terms.ts の `requireConsent`）が断る。画面の出し分けは飾り。
 */
export function ConsentDialog({ renewal, busy, error, onAgree, onLater }: Props) {
  const [checked, setChecked] = useState(false);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <div className="modal__panel modal__panel--narrow">
        <h2 id="consent-title" className="modal__title">{renewal ? '規約が新しくなりました' : 'はじめる前に'}</h2>
        <p className="modal__lead">
          {renewal
            ? '利用規約とプライバシーポリシーを更新しました。内容を確かめて、もう一度ご同意ください。'
            : 'アカウントを作るときに、利用規約とプライバシーポリシーへのご同意をお願いしています。'}
        </p>

        <label className="field field--check">
          <input type="checkbox" checked={checked} disabled={busy} onChange={(e) => setChecked(e.target.checked)} />
          <span>
            <Link to="/terms" target="_blank" rel="noreferrer">利用規約</Link>
            と
            <Link to="/privacy" target="_blank" rel="noreferrer">プライバシーポリシー</Link>
            に同意します
          </span>
        </label>

        {error && <p className="modal__error" role="alert">{error}</p>}

        <div className="page__actions">
          <button className="button button--primary" type="button" disabled={!checked || busy} onClick={onAgree}>
            {busy ? '記録しています…' : '同意して続ける'}
          </button>
          <button className="button button--ghost" type="button" onClick={onLater} disabled={busy}>あとで</button>
        </div>

        <p className="modal__fine">
          診断を受けるのも作るのも、同意がなくてもできます。いつ何の版に同意したかを記録します（氏名やメールアドレスは保存しません）。
        </p>
      </div>
    </div>
  );
}
